// hooks
import { type Pagination, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type { ILastProtection, IProtection, ReqCreateProtection } from '../model/protection.model';
import type { BaseApiResponse, BaseSearchRequest, CommonQueryOptions, ResultError } from 'modules';

// apis
import { ProtectionAPI } from '../apis/protection.api';

// keys
import { protectionKeys } from '../keys/protection.key';

export const protectionAPI = new ProtectionAPI();

/** 작업자 보호구 지급 이력 전체 조회 쿼리 훅 */
export const useGetProtections = ({
  data,
  options
}: {
  data: { userId: string; query?: BaseSearchRequest };
  options?: CommonQueryOptions<BaseApiResponse<unknown, IProtection>, ResultError, IProtection[]>;
}) => {
  const queryKeyFactory = (data: { userId: string; query?: BaseSearchRequest }) =>
    protectionKeys.GET_PROTECTIONS(data.userId, data.query);

  const queryFn = async (
    data: { userId: string; query?: BaseSearchRequest },
    pagination: Pagination
  ) => {
    const req = {
      ...data.query,
      page: pagination.current,
      size: pagination.pageSize
    };

    return await protectionAPI.getProtections(data.userId, req);
  };

  return useCommonQuery<
    { userId: string; query?: BaseSearchRequest },
    BaseApiResponse<unknown, IProtection>,
    ResultError,
    IProtection[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자 보호구 별 마지막 지급 이력 조회 쿼리 훅 */
export const useGetLastProtections = ({
  data,
  options
}: {
  data: string;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, ILastProtection>,
    ResultError,
    ILastProtection[]
  >;
}) => {
  const queryKeyFactory = (data: string) => protectionKeys.GET_LAST_PROTECTIONS(data);

  const queryFn = async (data: string) => {
    return await protectionAPI.getLastProtections(data);
  };

  return useCommonQuery<
    string,
    BaseApiResponse<unknown, ILastProtection>,
    ResultError,
    ILastProtection[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자 보호구 지급 회수 쿼리 훅 */
export const useDeleteProtection = () => {
  return useCommonMutation<BaseApiResponse<unknown>, ResultError, string>({
    mutationKey: protectionKeys.PROTECTION_ALL,
    mutationFn: async (data) => {
      return await protectionAPI.deleteProtection(data);
    },
    queryKeyToInvalidate: protectionKeys.PROTECTION_ALL
  });
};

/** 작업자 보호구 지급 쿼리 훅 */
export const useCreateProtection = () => {
  return useCommonMutation<BaseApiResponse<unknown>, ResultError, ReqCreateProtection>({
    mutationKey: protectionKeys.PROTECTION_ALL,
    mutationFn: async (data) => {
      return await protectionAPI.createProtection(data);
    },
    queryKeyToInvalidate: protectionKeys.PROTECTION_ALL
  });
};
